/**
 * Which paths under `/app` a visitor with no account may reach.
 *
 * ## Why this is a file rather than a condition
 *
 * It was an inline list of five `startsWith` calls inside `auth-provider.tsx`, and `App.tsx` had
 * the routes. Two copies of one fact, and the copy that mattered was invisible — a public route
 * added to the router without a matching exemption renders for exactly as long as it takes the
 * redirector's effect to fire. `apps/organizer/src/auth/public-paths.ts` was extracted for the
 * same reason and is tested the same way.
 *
 * ## Two kinds of entry, and they are public for different reasons
 *
 * **The auth flows** are public because they are what *creates* a session: the login page, the
 * registration wizard whose step 1 makes the account (`MEM-002`), recovery, the MFA challenge,
 * and the `/app/auth/*` exchanges — which must be exempt or a redirect fired mid-exchange burns a
 * single-use code.
 *
 * **Discovery** is public because the product says so. `MEM-019` AC-1: the Discover list, search,
 * the atmosphere filter and every public event's detail page are "available with no account", and
 * `MEM-AUTH-012`'s one-line version is "discovery is open; relationship data is not". A visitor
 * should reach an event from a shared link and decide to come before deciding to join.
 *
 * **This list is a client-side convenience and not a control.** Nothing here decides what data a
 * caller receives; the API does that, unauthenticated, with its own tests
 * (`MEM-019` AC-7 — "every gate is enforced on the session server-side, never by which
 * application the request came from"). Getting this list wrong shows a signed-out visitor a
 * screen that then has nothing to put on it; it does not disclose anything.
 */

export const MEMBER_APP_PUBLIC_PATHS: ReadonlyArray<string> = [
  '/app/login',
  '/app/join',
  '/app/recovery',
  '/app/mfa',
  // `USR-013`. Somebody an organizer added to an event, opening a mailed link to turn the place
  // held for them into an account. Named **exactly** rather than as `/app/account/` — that prefix
  // would make My Account and the privacy screen public, and this file would be doing the
  // opposite of its job.
  '/app/account/set-up',
  // `ADM-039` AC-3 (ruling 83, 2026-09-13). **This entry says less than it looks like it says.**
  //
  // The comment above used to end "`/app/account/verify-email` next door stays protected on
  // purpose: its endpoints authenticate on the caller's own token." That is still true of the
  // screen's `?code=` branch and is still what gates it — `MemberVerifyEmailRoute` wraps that
  // branch in `ProtectedRoute`, which renders the interstitial and claims the path, so a
  // signed-out member with no token is turned away exactly as before.
  //
  // What changed is that a *second* credential now lands on the same address. An administrator
  // can send a member the ordinary address-verification mail, and it carries an `address-proof`
  // credential link (ADR 042) rather than a code: `?token=<rowId>.<secret>`, spent by the public
  // `POST /organizers/onboarding/email-confirmations`, which takes no session because the token
  // **is** the credential. Its reader opened an inbox on whatever device was to hand and is very
  // often signed out — which is frequently *why* the mail was sent.
  //
  // The exemption has to be here rather than left to the gate, because the redirector above the
  // router fires for any signed-out visitor on a non-public `/app` path and only stands down for
  // a path a mounted `ProtectedRoute` has claimed. On the token branch there is no gate to claim
  // it, so without this line the member is navigated to the login form mid-render and the mail is
  // useless to the population it exists for.
  //
  // Nothing is disclosed by it. This list decides no data; the API does, and this path's two
  // endpoints go on authenticating exactly as they did.
  '/app/account/verify-email',
  '/app/auth/',
  // Discovery (`MEM-019` AC-1). `/app` exactly is Discover itself — matched as an exact path
  // below rather than as a prefix, because `/app/` as a prefix would make the whole member app
  // public and this file would be doing the opposite of its job.
  '/app/events/',
  // The organizer public profile inside the app shell (`MEM-018` AC-6). Public for the same
  // reason the event that links to it is: a visitor following a shared event link is often
  // signed out, and the organizer behind it is the same open fact. The Follow control is the
  // part that needs a member, and it withholds itself rather than the page.
  '/app/organizers/',
] as const;

export function isMemberAppPublicPath(pathname: string): boolean {
  if (pathname === '/app' || pathname === '/app/') return true;
  return MEMBER_APP_PUBLIC_PATHS.some((prefix) => pathname.startsWith(prefix));
}
